import React, { useState, useEffect } from 'react';
import { SectionItem, ItemType, Language } from '../../../types/pageBuilder';
import { itemsService } from '../../../services/itemsService';
import ItemEditor from './ItemEditor';
import { GripVertical, Eye, EyeOff, Edit2, Copy, Trash2, Plus, Loader } from 'lucide-react';

interface ItemsListProps {
  sectionId: number;
  itemType: ItemType;
  language: Language;
  onItemsChange: () => void;
}

/**
 * Visual list manager for array items (features, steps, etc.)
 */
const ItemsList: React.FC<ItemsListProps> = ({
  sectionId,
  itemType,
  language,
  onItemsChange,
}) => {
  const [items, setItems] = useState<SectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<SectionItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [draggedItem, setDraggedItem] = useState<SectionItem | null>(null);

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, language]);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedItems = await itemsService.getItems(sectionId, language);
      setItems(fetchedItems.sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (item: SectionItem) => {
    try {
      await itemsService.updateItem(item.id, { is_visible: !item.is_visible });
      await loadItems();
      onItemsChange();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update visibility');
    }
  };

  const handleDelete = async (item: SectionItem) => {
    if (!window.confirm(`Are you sure you want to delete this ${itemType}?`)) return;

    try {
      await itemsService.deleteItem(item.id);
      await loadItems();
      onItemsChange();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete item');
    }
  };

  const handleDuplicate = async (item: SectionItem) => {
    try {
      await itemsService.duplicateItem(item.id);
      await loadItems();
      onItemsChange();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to duplicate item');
    }
  };

  const handleSaveItem = async (itemData: any) => {
    try {
      if (editingItem) {
        // Update existing item
        await itemsService.updateItem(editingItem.id, itemData);
      } else {
        // Create new item
        await itemsService.createItem({
          section_id: sectionId,
          item_type: itemType,
          ...itemData,
        });
      }
      setEditingItem(null);
      setIsCreating(false);
      await loadItems();
      onItemsChange();
    } catch (err) {
      throw err; // Let ItemEditor handle the error
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, item: SectionItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetItem: SectionItem) => {
    e.preventDefault();

    if (!draggedItem || draggedItem.id === targetItem.id) return;

    const newItems = [...items];
    const draggedIndex = newItems.findIndex((i) => i.id === draggedItem.id);
    const targetIndex = newItems.findIndex((i) => i.id === targetItem.id);

    // Remove dragged item and insert at target position
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, removed);

    // Update display orders
    const orderData = newItems.map((item, index) => ({
      id: item.id,
      display_order: index + 1,
    }));

    try {
      await itemsService.reorderItems(sectionId, orderData);
      setItems(newItems);
      onItemsChange();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reorder items');
      await loadItems(); // Reload on error
    } finally {
      setDraggedItem(null);
    }
  };

  // Render icon
  const renderIcon = (item: SectionItem) => {
    if (!item.icon) return null;

    switch (item.icon.type) {
      case 'svg':
        return (
          <div
            className="w-8 h-8 flex-shrink-0"
            dangerouslySetInnerHTML={{ __html: item.icon.code }}
          />
        );
      case 'emoji':
        return <span className="text-2xl flex-shrink-0">{item.icon.code}</span>;
      case 'fontawesome':
        return <i className={`${item.icon.code} text-xl flex-shrink-0`} />;
      case 'image':
        return (
          <img
            src={item.icon.code}
            alt={item.icon.display_name}
            className="w-8 h-8 object-contain flex-shrink-0"
          />
        );
      default:
        return null;
    }
  };

  const getItemTitle = (item: SectionItem): string => {
    const content = item.content[language];

    // Safety: Extract value if it's an object with {value, type} structure
    const extractValue = (field: any): string => {
      if (!field) return '';
      if (typeof field === 'string') return field;
      if (typeof field === 'number') return String(field);
      if (typeof field === 'object' && field.value !== undefined) {
        return typeof field.value === 'string' || typeof field.value === 'number'
          ? String(field.value)
          : '';
      }
      return '';
    };

    return extractValue(content.title) || extractValue(content.question) || extractValue(content.label) || 'Untitled';
  };

  const getItemDescription = (item: SectionItem): string => {
    const content = item.content[language];

    // Safety: Extract value if it's an object with {value, type} structure
    const extractValue = (field: any): string => {
      if (!field) return '';
      if (typeof field === 'string') return field;
      if (typeof field === 'number') return String(field);
      if (typeof field === 'object' && field.value !== undefined) {
        return typeof field.value === 'string' || typeof field.value === 'number'
          ? String(field.value)
          : '';
      }
      return '';
    };

    return extractValue(content.description) || extractValue(content.answer) || extractValue(content.number) || '';
  };

  const getItemTypeLabel = (type: ItemType): string => {
    const labels: Record<ItemType, string> = {
      feature: 'Feature',
      step: 'Step',
      value: 'Value',
      faq: 'FAQ',
      stat: 'Statistic',
      contact_method: 'Contact Method',
      highlight: 'Highlight',
    };
    return labels[type];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadItems}
          className="mt-2 text-blue-500 hover:text-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">
          {getItemTypeLabel(itemType)}s ({items.length} {items.length === 1 ? 'item' : 'items'})
        </h4>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus size={16} />
          Add {getItemTypeLabel(itemType)}
        </button>
      </div>

      {/* Items List */}
      {items.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-500 mb-4">No {itemType}s added yet</p>
          <button
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Add First {getItemTypeLabel(itemType)}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, item)}
              className={`
                bg-white border-2 rounded-lg p-4 transition-all cursor-move
                ${draggedItem?.id === item.id ? 'opacity-50 border-blue-400' : 'border-gray-200'}
                ${!item.is_visible ? 'opacity-60' : ''}
                hover:border-blue-300 hover:shadow-sm
              `}
            >
              <div className="flex items-start gap-3">
                {/* Drag Handle */}
                <GripVertical className="text-gray-400 flex-shrink-0 mt-1 cursor-grab active:cursor-grabbing" size={20} />

                {/* Icon */}
                {item.icon && <div className="mt-1">{renderIcon(item)}</div>}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h5 className="font-medium text-gray-900 truncate">{getItemTitle(item)}</h5>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                    {getItemDescription(item)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleVisibility(item)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title={item.is_visible ? 'Hide' : 'Show'}
                  >
                    {item.is_visible ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                  <button
                    onClick={() => setEditingItem(item)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDuplicate(item)}
                    className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                    title="Duplicate"
                  >
                    <Copy size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Item Editor Modal */}
      {(editingItem || isCreating) && (
        <ItemEditor
          item={editingItem}
          itemType={itemType}
          language={language}
          onSave={handleSaveItem}
          onCancel={() => {
            setEditingItem(null);
            setIsCreating(false);
          }}
        />
      )}
    </div>
  );
};

export default ItemsList;
